// Shared fetch-based SSE helpers. EventSource can't send an Authorization
// header, so the streaming hooks read the body with fetch instead.

export type SseFrame = { kind: 'comment'; text: string } | { kind: 'event'; event: string; data: string; id?: string };

export function sseHeaders(token: string | null): Record<string, string> {
  return {
    // The Flask check_request middleware rejects any /api/* request whose
    // Accept "best" isn't application/json. We still want SSE for success
    // responses (server forces text/event-stream Content-Type regardless),
    // so list JSON first to satisfy the check while keeping event-stream
    // as a secondary preference.
    Accept: 'application/json, text/event-stream',
    'X-Requested-With': 'XMLHTTPRequest',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function parseFrame(frame: string): SseFrame | null {
  if (frame.startsWith(':')) return { kind: 'comment', text: frame.slice(1).trim() };
  if (!frame.trim()) return null;
  let event = 'message';
  let id: string | undefined;
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    else if (line.startsWith('id:')) id = line.slice(3).trim();
  }
  return id === undefined
    ? { kind: 'event', event, data: dataLines.join('\n') }
    : { kind: 'event', event, data: dataLines.join('\n'), id };
}

export interface ReadSseOptions {
  // Return true to stop reading.
  onFrame: (frame: SseFrame) => boolean | void;
  // Called for every chunk read, before its frames.
  onChunk?: () => void;
}

/**
 * Read an SSE body until it ends, onFrame asks to stop, or something throws.
 * Resolves true if the server closed the stream, false if onFrame stopped it.
 * Errors from read() or onFrame propagate. The body is always cancelled on
 * the way out, so a stream the caller stopped reading is not left open.
 */
export async function readSseStream(
  body: ReadableStream<Uint8Array>,
  { onFrame, onChunk }: ReadSseOptions,
): Promise<boolean> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) return true;
      onChunk?.();
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = parseFrame(buffer.slice(0, sep));
        buffer = buffer.slice(sep + 2);
        if (frame && onFrame(frame)) return false;
      }
    }
  } finally {
    reader.cancel().catch(() => undefined);
  }
}
